import React, { useState } from 'react';
import { X } from 'lucide-react';

interface ClientTaskFormProps {
  stepId: number;
  stepTypeId: number;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

export default function ClientTaskForm({ stepId, stepTypeId, onSubmit, onCancel }: ClientTaskFormProps) {
  const [task, setTask] = useState({
    name: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    assigned_to: '',
    assigned_to_type: 'client',
    due_date: '',
    estimated_hours: 0,
    // Client-specific fields
    client_email: '',
    client_phone: '',
    requires_client_approval: true,
    client_deadline: '',
    external_link: '',
    client_instructions: '',
    notification_preference: 'email' // email, sms, both
  });

  const handleSubmit = () => {
    if (!task.name) {
      alert('Please enter a task name');
      return;
    }

    const config = {
      client_email: task.client_email,
      client_phone: task.client_phone,
      requires_client_approval: task.requires_client_approval,
      client_deadline: task.client_deadline,
      external_link: task.external_link,
      client_instructions: task.client_instructions,
      notification_preference: task.notification_preference
    };

    onSubmit({
      step_type_id: stepTypeId,
      name: task.name,
      description: task.description,
      status: task.status,
      priority: task.priority,
      assigned_to: task.assigned_to,
      assigned_to_type: task.assigned_to_type,
      due_date: task.due_date,
      estimated_hours: task.estimated_hours,
      config: JSON.stringify(config)
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Create Client Task</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Task Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Task Name *
            </label>
            <input
              type="text"
              value={task.name}
              onChange={(e) => setTask({ ...task, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="e.g., Review and approve design mockups"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={task.description}
              onChange={(e) => setTask({ ...task, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Detailed description of what the client needs to do..."
            />
          </div>

          {/* Client Information */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3">Client Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Contact Name
                </label>
                <input
                  type="text"
                  value={task.assigned_to}
                  onChange={(e) => setTask({ ...task, assigned_to: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Email
                </label>
                <input
                  type="email"
                  value={task.client_email}
                  onChange={(e) => setTask({ ...task, client_email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="client@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Phone (Optional)
                </label>
                <input
                  type="tel"
                  value={task.client_phone}
                  onChange={(e) => setTask({ ...task, client_phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notification Preference
                </label>
                <select
                  value={task.notification_preference}
                  onChange={(e) => setTask({ ...task, notification_preference: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="both">Both</option>
                </select>
              </div>
            </div>
          </div>

          {/* Task Details */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3">Task Details</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={task.due_date}
                  onChange={(e) => setTask({ ...task, due_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Deadline (if different)
                </label>
                <input
                  type="date"
                  value={task.client_deadline}
                  onChange={(e) => setTask({ ...task, client_deadline: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  value={task.priority}
                  onChange={(e) => setTask({ ...task, priority: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Hours
                </label>
                <input
                  type="number"
                  value={task.estimated_hours}
                  onChange={(e) => setTask({ ...task, estimated_hours: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg"
                  min="0"
                  step="0.5"
                />
              </div>
            </div>
          </div>

          {/* Additional Options */}
          <div className="border-t pt-4">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  External Link (Optional)
                </label>
                <input
                  type="url"
                  value={task.external_link}
                  onChange={(e) => setTask({ ...task, external_link: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="https://..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Link to shared document, Figma, staging site, etc.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instructions for Client
                </label>
                <textarea
                  value={task.client_instructions}
                  onChange={(e) => setTask({ ...task, client_instructions: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Specific instructions or questions for the client..."
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="requires_approval"
                  checked={task.requires_client_approval}
                  onChange={(e) => setTask({ ...task, requires_client_approval: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="requires_approval" className="text-sm text-gray-700">
                  Requires formal client approval
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-6 pt-4 border-t">
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create Client Task
          </button>
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
